import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import "aos/dist/aos.css";
import { Gift, ShieldCheck, Smartphone, Target, UserX, Zap, MonitorPlay } from "lucide-react";
import { Helmet } from "react-helmet-async"; 

const FreeGames = () => {

  useEffect(() => {
  import("aos").then((AOS) => {
    AOS.default.init({
      duration: 500,
      once: true,
      offset: 80,
      easing: "ease-out-cubic",
    });
  });
}, []);

// 🚀 NATURAL HUMAN WRITTEN CONTENT FOR HIGH VALUE
const featuresList = [
  { 
    title: "Always Free to Play", 
    desc: "We don't like hidden costs either. Every game on Game Zone Hub is completely free. You don't have to worry about hitting a paywall halfway through a level or needing a credit card to unlock new features." 
  },
  { 
    title: "No Waiting Around", 
    desc: "Because our games are built for the web, you don't have to wait for large files to download or install. Just pick a game you like, click play, and it loads almost instantly in your browser." 
  },
  { 
    title: "Something for Everyone", 
    desc: "Whether you want to relax with a simple puzzle, race high-speed cars, or compete in an action-packed multiplayer match, our collection is big enough to match whatever mood you are in." 
  },
  { 
    title: "Safe and Family Friendly", 
    desc: "We carefully check every game before we put it on our site. Since you are playing directly in the browser, there is no risk of downloading viruses. It is a secure place for anyone to spend their free time." 
  },
  { 
    title: "Play Without Logging In", 
    desc: "You shouldn't have to remember another password just to play a game. We do not force you to create an account or give us your email address. You can stay anonymous and still enjoy all our games." 
  },
  { 
    title: "Works on Your Phone", 
    desc: "Sitting on the train or waiting for an appointment? You can play our games on your iPhone or Android device. The games automatically resize to fit your screen and work perfectly with touch controls." 
  }
];

const schemaData = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": "Why Play Games at Game Zone Hub?",
  "itemListElement": featuresList.map((feature, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": feature.title,
    "description": feature.desc
  }))
};


  return (
    <section 
      className="relative bg-gradient-to-b lg:px-8 from-[#0b1120] to-[#141a33] sm:pt-[80px] pt-10 text-white sm:pb-16 pb-10"
    >
      <Helmet>
        {schemaData && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
        )}
      </Helmet>
      <div className="container mx-auto px-4">

        <div className="text-center sm:mb-16 mb-10">
            <h2 
            className="text-2xl sm:text-3xl md:text-4xl xl:text-5xl font-extrabold mb-6"
            data-aos="fade-down"
            data-aos-delay="100"
            >
            Why Play on <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-300">Game Zone Hub?</span>
            </h2>

            <p 
            className="sm:text-lg text-[16px] text-gray-300 max-w-4xl mx-auto leading-relaxed"
            data-aos="fade-up"
            data-aos-delay="200"
            >
            We think playing games should be the easiest part of your day. No annoying apps to install, no complicated sign-ups, and definitely no hidden fees. Game Zone Hub is just a simple, fast, and safe place to find great <strong>free online games</strong> and start playing immediately, no matter what device you are using.
            </p>
        </div>

        {/* FEATURES GRID */}
        <ul className="grid lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-8 sm:mb-20 mb-10">
        
        <li data-aos="zoom-in-up" data-aos-delay="100" className="h-full">
          <div className="group bg-[#1e293b] h-full p-8 rounded-3xl border border-white/5 
            transition-all duration-300 hover:-translate-y-2 hover:scale-[1.03]
            hover:shadow-[0_10px_30px_rgba(59,130,246,0.25)] hover:border-blue-500/50 flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center mb-6 group-hover:bg-blue-500/20 group-hover:scale-110 transition-all duration-300">
                <Gift className="w-8 h-8 text-blue-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-4">
              {featuresList[0].title}
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed flex-grow">
              {featuresList[0].desc}
            </p>
          </div>
        </li>

        <li data-aos="zoom-in-up" data-aos-delay="200" className="h-full">
          <div className="group bg-[#1e293b] h-full sm:p-8 p-6 rounded-3xl border border-white/5 
            transition-all duration-300 hover:-translate-y-2 hover:scale-[1.03]
            hover:shadow-[0_10px_30px_rgba(234,179,8,0.25)] hover:border-yellow-500/50 flex flex-col items-center text-center">
             <div className="w-16 h-16 bg-yellow-500/10 rounded-full flex items-center justify-center mb-6 group-hover:bg-yellow-500/20 group-hover:scale-110 transition-all duration-300">
                <Zap className="w-8 h-8 text-yellow-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-4">
              {featuresList[1].title}
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed flex-grow">
              {featuresList[1].desc}
            </p>
          </div>
        </li>

        <li data-aos="zoom-in-up" data-aos-delay="300" className="h-full">
          <div className="group bg-[#1e293b] h-full sm:p-8 p-6 rounded-3xl border border-white/5 
            transition-all duration-300 hover:-translate-y-2 hover:scale-[1.03]
            hover:shadow-[0_10px_30px_rgba(236,72,153,0.25)] hover:border-pink-500/50 flex flex-col items-center text-center">
             <div className="w-16 h-16 bg-pink-500/10 rounded-full flex items-center justify-center mb-6 group-hover:bg-pink-500/20 group-hover:scale-110 transition-all duration-300">
                <Target className="w-8 h-8 text-pink-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-4">
              {featuresList[2].title}
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed flex-grow">
              {featuresList[2].desc}
            </p>
          </div>
        </li>

        <li data-aos="zoom-in-up" data-aos-delay="400" className="h-full">
          <div className="group bg-[#1e293b] h-full sm:p-8 p-6 rounded-3xl border border-white/5 
            transition-all duration-300 hover:-translate-y-2 hover:scale-[1.03]
            hover:shadow-[0_10px_30px_rgba(34,197,94,0.25)] hover:border-green-500/50 flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mb-6 group-hover:bg-green-500/20 group-hover:scale-110 transition-all duration-300">
                <ShieldCheck className="w-8 h-8 text-green-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-4">
              {featuresList[3].title}
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed flex-grow">
             {featuresList[3].desc}
            </p>
          </div>
        </li>

        <li data-aos="zoom-in-up" data-aos-delay="500" className="h-full">
          <div className="group bg-[#1e293b] h-full sm:p-8 p-6 rounded-3xl border border-white/5 
            transition-all duration-300 hover:-translate-y-2 hover:scale-[1.03]
            hover:shadow-[0_10px_30px_rgba(168,85,247,0.25)] hover:border-purple-500/50 flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-purple-500/10 rounded-full flex items-center justify-center mb-6 group-hover:bg-purple-500/20 group-hover:scale-110 transition-all duration-300">
                <UserX className="w-8 h-8 text-purple-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-4">
              {featuresList[4].title}
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed flex-grow">
              {featuresList[4].desc}
            </p>
          </div>
        </li>

        <li data-aos="zoom-in-up" data-aos-delay="600" className="h-full">
          <div className="group bg-[#1e293b] h-full sm:p-8 p-6 rounded-3xl border border-white/5 
            transition-all duration-300 hover:-translate-y-2 hover:scale-[1.03]
            hover:shadow-[0_10px_30px_rgba(34,211,238,0.25)] hover:border-cyan-500/50 flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-cyan-500/10 rounded-full flex items-center justify-center mb-6 group-hover:bg-cyan-500/20 group-hover:scale-110 transition-all duration-300">
                <Smartphone className="w-8 h-8 text-cyan-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-4">
              {featuresList[5].title}
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed flex-grow">
              {featuresList[5].desc}
            </p>
          </div>
        </li>

      </ul>

        {/* 🚀 NATURAL MINI-ARTICLE FOR SEO VALUE */}
        <div className="bg-[#0f172a] rounded-3xl p-8 sm:p-12 border border-white/5 shadow-2xl mb-12" data-aos="fade-up" data-aos-delay="300">
            <div className="flex flex-wrap items-center sm:justify-center gap-3 mb-6">
                <MonitorPlay className="text-blue-400 w-8 h-8" />
                <h3 className="text-2xl sm:text-3xl font-bold text-white">Browser Games Are Changing</h3>
            </div>
            <div className="space-y-4 text-gray-400 text-sm sm:text-base leading-relaxed text-center max-w-4xl mx-auto">
                <p>
                    A few years ago, playing games on your computer meant you had to buy an expensive system and wait hours for downloads to finish. But modern web browsers have gotten much smarter. Today, technologies like HTML5 let developers create amazing, fast-paced games that run right inside the window you are using to read this.
                </p>
                <p>
                    We built Game Zone Hub to collect all these great games in one place. We handle the technical side to make sure everything loads fast and runs securely, so you can just focus on having fun. Whether you have five minutes to spare or a whole afternoon, there is a game here waiting for you.
                </p>
            </div>
        </div>

        {/* CALL TO ACTION */}
        <div 
          className="text-center sm:pb-8"
          data-aos="fade-up"
          data-aos-delay="400"
        >
          <Link 
            to="/games" 
            aria-label="See all our games"
            className="inline-flex items-center gap-3 bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 sm:px-12 px-8 sm:py-5 py-4 rounded-full sm:text-lg text-[16px] font-extrabold text-white shadow-[0_0_25px_rgba(59,130,246,0.4)] transition-transform transform hover:scale-105 hover:shadow-[0_0_40px_rgba(59,130,246,0.6)]"
          >
            Start Playing Now
          </Link>
        </div>

      </div>
    </section>
  );
};

export default FreeGames;